/**
 * Instance profile loader (ADR-001 §8). The per-customer *shape* — tier,
 * branding, assistant chrome, enabled modules — is config-as-code: one
 * git-tracked file per instance under web/instances/<name>/config.ts,
 * selected at build time by VITE_INSTANCE (set in .env.<name>, loaded via
 * `vite --mode <name>`).
 *
 * Structure is static and versioned; the app never queries prod for it. Only
 * secrets/identity (coreBOS + kernel base URLs, API key) come from env, and
 * only data from the providers.
 */
export type ShellTier = "hub" | "portal";

export interface InstanceQuickAction {
  key: string;
  /** Ant icon name, resolved via getIconComponent in the shell. */
  icon: string;
  label: string;
  prompt: string;
  send?: boolean;
}

export interface InstanceAssistant {
  title: string;
  emptyState: string;
  placeholder: string;
  quickActions: InstanceQuickAction[];
}

export interface InstanceConfig {
  name: string;
  /** Which shell tier this instance mounts (ADR-001 §7). */
  tier: ShellTier;
  brand: string;
  logoSrc?: string;
  /** Enabled module keys; omit or empty = all modules. */
  modules?: string[];
  /** Portal-tier assistant chrome (ignored by the hub tier). */
  assistant?: InstanceAssistant;
}

const profiles = import.meta.glob("../instances/*/config.ts", {
  eager: true,
  import: "default",
}) as Record<string, InstanceConfig>;

const name = import.meta.env.VITE_INSTANCE || "ops";

function pick(): InstanceConfig {
  const hit =
    profiles[`../instances/${name}/config.ts`] ??
    profiles["../instances/ops/config.ts"];
  if (!hit) {
    throw new Error(
      `No instance profile for "${name}" and no ops default profile found`,
    );
  }
  return hit;
}

export const instance: InstanceConfig = pick();
